import { useEffect, useState } from "react";

import { PHONE_PREVIEW_CONFIG } from "./LivePreview/helpers/constants";

const MIN_LEGIBLE_PHONE_HEIGHT = 200;

type UseResponsivePhonePreviewOptions = {
  verticalChrome?: number;
};

export const useResponsivePhonePreview = ({ verticalChrome }: UseResponsivePhonePreviewOptions = {}) => {
  const [viewportHeight, setViewportHeight] = useState<number>(
    typeof window !== "undefined" ? window.innerHeight : 900
  );

  useEffect(() => {
    const updateViewportHeight = () => {
      setViewportHeight(window.innerHeight);
    };

    updateViewportHeight();
    window.addEventListener("resize", updateViewportHeight);
    return () => {
      window.removeEventListener("resize", updateViewportHeight);
    };
  }, []);

  // Calculate phone height using linear interpolation based on viewport height
  const phoneHeight = (() => {
    const minViewportHeight = PHONE_PREVIEW_CONFIG.viewport.min;
    const maxViewportHeight = PHONE_PREVIEW_CONFIG.viewport.max;
    const minPhoneHeight = PHONE_PREVIEW_CONFIG.height.min;
    const maxPhoneHeight = PHONE_PREVIEW_CONFIG.height.max;

    const clampedViewportHeight = Math.max(minViewportHeight, Math.min(maxViewportHeight, viewportHeight));
    const viewportRange = maxViewportHeight - minViewportHeight;
    const phoneHeightRange = maxPhoneHeight - minPhoneHeight;
    const viewportProgress = (clampedViewportHeight - minViewportHeight) / viewportRange;
    const interpolatedHeight = Math.round(minPhoneHeight + viewportProgress * phoneHeightRange);

    const availableHeight = Math.max(
      viewportHeight - (verticalChrome ?? PHONE_PREVIEW_CONFIG.verticalChrome),
      MIN_LEGIBLE_PHONE_HEIGHT
    );
    return Math.min(interpolatedHeight, availableHeight);
  })();

  const phoneAspectRatio = PHONE_PREVIEW_CONFIG.originalWidth / PHONE_PREVIEW_CONFIG.originalHeight;
  const phoneWidth = phoneHeight * phoneAspectRatio;
  const phoneScale = phoneHeight / PHONE_PREVIEW_CONFIG.originalHeight;

  return {
    phoneHeight,
    phoneWidth,
    phoneScale,
    originalWidth: PHONE_PREVIEW_CONFIG.originalWidth,
    originalHeight: PHONE_PREVIEW_CONFIG.originalHeight,
    aspectRatio: phoneAspectRatio,
  };
};
